/**
 * Primitive Layer Renderer
 * Renders primitive components (moon, stars, badges, etc.)
 */

import React, { useRef, useState, useLayoutEffect } from 'react';
import type { PrimitiveLayer } from '@/types/layers';
import type { ResolvedPosition } from '@/lib/position/resolver';
import { resolveVariables } from '@/types/template';
import { getPrimaryColor } from '@/types/brush';

// Import primitive components
import { EclipseMoon } from '@/components/primitives/EclipseMoon';
import { StarField } from '@/components/primitives/StarField';
import { Wordmark } from '@/components/primitives/Wordmark';
import { AppStoreBadges } from '@/components/primitives/AppStoreBadges';
import { CTAButton } from '@/components/primitives/CTAButton';

interface PrimitiveLayerRendererProps {
  layer: PrimitiveLayer;
  position: ResolvedPosition;
  variableValues: Record<string, string>;
  isSelected?: boolean;
  isHovered?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function PrimitiveLayerRenderer({
  layer,
  position,
  variableValues,
  isSelected,
  isHovered,
  onClick,
  onMouseDown,
  onMouseEnter,
  onMouseLeave,
}: PrimitiveLayerRendererProps) {
  const { content, transform, opacity, width, height } = layer;
  const { primitiveType, size, scale, color, props } = content;

  // Ref to measure natural content size
  const contentRef = useRef<HTMLDivElement>(null);
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);

  // Measure natural size of content (offsetWidth/Height gives pre-transform size)
  useLayoutEffect(() => {
    if (contentRef.current) {
      setNaturalSize({
        width: contentRef.current.offsetWidth,
        height: contentRef.current.offsetHeight,
      });
    }
  }, [primitiveType, size, scale, props]);

  // Resolve variable binding for CTA text
  const ctaText = content.variableBinding
    ? resolveVariables(content.variableBinding, variableValues)
    : content.text ?? 'Get Started';

  // Calculate scale factor if explicit dimensions are set
  const targetWidth = position.width ?? width;
  const targetHeight = position.height ?? height;
  const hasExplicitSize = typeof targetWidth === 'number' && typeof targetHeight === 'number';

  let contentScale = 1;
  if (hasExplicitSize && naturalSize && naturalSize.width > 0 && naturalSize.height > 0) {
    // Scale uniformly to fit within target dimensions
    const scaleX = targetWidth / naturalSize.width;
    const scaleY = targetHeight / naturalSize.height;
    contentScale = Math.min(scaleX, scaleY);
  }

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    left: position.x,
    top: position.y,
    width: hasExplicitSize ? targetWidth : 'auto',
    height: hasExplicitSize ? targetHeight : 'auto',
    opacity,
    transform: transform
      ? `rotate(${transform.rotation ?? 0}deg) scale(${transform.scaleX ?? 1}, ${transform.scaleY ?? 1})`
      : undefined,
    transformOrigin: transform
      ? `${(transform.originX ?? 0.5) * 100}% ${(transform.originY ?? 0.5) * 100}%`
      : undefined,
    // Hover styling (selection handled by SelectionOverlay)
    outline: isHovered && !isSelected ? '1px dashed #3B82F6' : 'none',
    outlineOffset: 2,
    cursor: 'pointer',
    pointerEvents: layer.locked ? 'none' : 'auto',
    // For explicit sizing, we'll use overflow hidden and scale content
    overflow: hasExplicitSize ? 'hidden' : undefined,
  };

  // Style for scaling the content within the container
  const contentWrapperStyle: React.CSSProperties = hasExplicitSize && contentScale !== 1 ? {
    transform: `scale(${contentScale})`,
    transformOrigin: 'top left',
  } : {};

  // Convert Brush color to string for primitive components
  const colorValue = color ? getPrimaryColor(color) : undefined;

  const renderPrimitive = () => {
    switch (primitiveType) {
      case 'eclipse-moon':
        return <EclipseMoon size={size ?? 280} />;

      case 'star-field':
        return <StarField count={content.starCount ?? 120} />;

      case 'wordmark':
        return <Wordmark size={size ?? 28} color={colorValue} />;

      case 'app-badges':
        return <AppStoreBadges scale={scale ?? 1} />;

      case 'cta-button':
        return (
          <CTAButton
            text={ctaText}
            variant={(props?.variant as 'glow' | 'primary' | 'outline') ?? 'glow'}
            size={(props?.size as 'sm' | 'md' | 'lg') ?? 'md'}
            showArrow={props?.showArrow as boolean}
          />
        );

      case 'data-leak':
        // DataLeak component would be rendered here
        return <div style={{ color: colorValue ?? '#EF4444' }}>Data Leak Visualization</div>;

      default:
        return <div>Unknown primitive: {primitiveType}</div>;
    }
  };

  // Star field needs special handling - it fills its container
  if (primitiveType === 'star-field') {
    return (
      <div
        style={{
          ...containerStyle,
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          left: 0,
          top: 0,
          pointerEvents: 'none',
        }}
        data-layer-id={layer.id}
      >
        {renderPrimitive()}
      </div>
    );
  }

  return (
    <div
      style={containerStyle}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-layer-id={layer.id}
    >
      <div ref={contentRef} style={contentWrapperStyle}>
        {renderPrimitive()}
      </div>
    </div>
  );
}
